import { useMemo } from 'react';
import { Handle, HandleProps, getConnectedEdges, getIncomers, getOutgoers, useNodeId, useReactFlow, useStore } from 'reactflow';
import { FunnelFunctions, NodeVariants } from '../../co-pilot.types';

const selector = (s: any) => ({
  nodeInternals: s.nodeInternals,
  edges: s.edges,
});

type CustomHandleProps = Omit<HandleProps, 'isConnectable'> & {
  isConnectable: any;
};

const CustomHandle: React.FC<CustomHandleProps & Omit<React.HTMLAttributes<HTMLDivElement>, 'id'>> = (props) => {
  const { nodeInternals, edges } = useStore(selector);
  const { getNodes } = useReactFlow();
  const nodeId = useNodeId();

  const isHandleConnectable = useMemo(() => {
    if (typeof props.isConnectable === 'function') {
      const node = nodeInternals.get(nodeId);
      const connectedEdges = getConnectedEdges([node], edges);

      return props.isConnectable({ node, connectedEdges });
    }

    if (typeof props.isConnectable === 'number') {
      const node = nodeInternals.get(nodeId);
      const nodes = getNodes();
      const connectedEdges = getConnectedEdges([node], edges);

      if (props.type === 'target') {
        const incomers = getIncomers(node, nodes, edges);

        return incomers.length < props.isConnectable;
      }

      if (props.type === 'source') {
        const outgoers = getOutgoers(node, nodes, edges);

        if (
          node.type === NodeVariants.CONDITIONAL_NODE ||
          node.type === NodeVariants.CONDITION_WITH_DURATION_NODE ||
          (node.type === NodeVariants.UTILITY_NODE && node.data.functionName === FunnelFunctions.CHECK_IF_GENERATED_EMAIL_ADDRESS)
        ) {
          let hasConnection = false;
          outgoers.forEach((outgoer) => {
            if (connectedEdges.some((edge) => edge.sourceHandle === props.id && outgoer.id === edge.target)) {
              hasConnection = true;
            }
          });

          return !hasConnection;
        }

        return outgoers.length < props.isConnectable;
      }

      return connectedEdges.length < props.isConnectable;
    }

    return props.isConnectable;
  }, [nodeInternals, edges, nodeId, props.isConnectable]);

  return (
    <Handle {...props} isConnectable={isHandleConnectable}>
      {props.children}
    </Handle>
  );
};

export default CustomHandle;
